/**
 * Tier Progression Visualization
 *
 * Visual timeline showing path to higher CRO Card tiers
 */

import { useMemo } from 'react';
import { Icons } from '../Icons';
import type { CROCard } from '../../lib/api/cryptocom-api';
import { fmt } from '../../utils/formatters';

interface TierProgressionVisualizationProps {
  currentCard?: CROCard | null;
  croPrice?: number;
}

interface Tier {
  tier: CROCard['tier'];
  name: string;
  requiredStake: number;
  stakingAPR: number;
  cashbackRate: number;
  color: string;
  benefits: string[];
}

const TIERS: Tier[] = [
  {
    tier: 'MIDNIGHT_BLUE',
    name: 'Midnight Blue',
    requiredStake: 0,
    stakingAPR: 0,
    cashbackRate: 1,
    color: '#1e3a8a',
    benefits: ['1% Cashback']
  },
  {
    tier: 'RUBY_STEEL',
    name: 'Ruby Steel',
    requiredStake: 400,
    stakingAPR: 4,
    cashbackRate: 2,
    color: '#dc2626',
    benefits: ['2% Cashback', 'Spotify Rebate']
  },
  {
    tier: 'ROYAL_INDIGO',
    name: 'Royal Indigo',
    requiredStake: 4000,
    stakingAPR: 6,
    cashbackRate: 3,
    color: '#7c3aed',
    benefits: ['3% Cashback', 'Spotify + Netflix']
  },
  {
    tier: 'ICY_WHITE',
    name: 'Icy White',
    requiredStake: 40000,
    stakingAPR: 8,
    cashbackRate: 5,
    color: '#d1d5db',
    benefits: ['5% Cashback', 'All Rebates', 'Airport Lounge']
  },
  {
    tier: 'OBSIDIAN',
    name: 'Obsidian',
    requiredStake: 400000,
    stakingAPR: 8,
    cashbackRate: 8,
    color: '#111827',
    benefits: ['8% Cashback', 'Premium Benefits', 'Private Jet']
  }
];

export function TierProgressionVisualization({ currentCard, croPrice = 0.10 }: TierProgressionVisualizationProps) {
  const currentTierIndex = useMemo(() => {
    if (!currentCard) return 0;
    return TIERS.findIndex(t => t.tier === currentCard.tier);
  }, [currentCard]);

  const currentStake = currentCard?.stakedAmount || 0;

  // Calculate progress to next tier
  const currentTier = TIERS[currentTierIndex];
  const nextTier = currentTierIndex < TIERS.length - 1 ? TIERS[currentTierIndex + 1] : null;

  const progressToNext = useMemo(() => {
    if (!nextTier) return 100;
    const range = nextTier.requiredStake - currentTier.requiredStake;
    const progress = currentStake - currentTier.requiredStake;
    return Math.min(100, (progress / range) * 100);
  }, [currentTier, nextTier, currentStake]);

  const croNeededForNext = nextTier ? Math.max(0, nextTier.requiredStake - currentStake) : 0;
  const usdNeededForNext = croNeededForNext * croPrice;

  return (
    <div className="card">
      <h2 style={{ marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Icons.TrendingUp size={24} />
        Progression des Tiers CRO Card
      </h2>

      {/* Current Status */}
      {currentCard && (
        <div style={{
          padding: '16px',
          background: `linear-gradient(135deg, ${currentTier.color}20 0%, ${currentTier.color}10 100%)`,
          borderRadius: '8px',
          border: `2px solid ${currentTier.color}`,
          marginBottom: '24px'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <div>
              <div style={{ fontSize: '14px', color: 'var(--muted)', marginBottom: '4px' }}>
                Votre tier actuel
              </div>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: currentTier.color }}>
                {currentTier.name}
              </div>
            </div>
            <div style={{
              padding: '12px 20px',
              background: currentTier.color,
              borderRadius: '8px',
              color: currentTier.tier === 'ICY_WHITE' ? '#000' : '#fff',
              fontWeight: 'bold',
              fontSize: '20px'
            }}>
              {currentCard.cashbackRate}%
            </div>
          </div>
          <div style={{ fontSize: '13px', color: 'var(--muted)' }}>
            {fmt.number(currentStake)} CRO stakés ({fmt.currency(currentStake * croPrice)})
          </div>
        </div>
      )}

      {/* Next Tier Preview */}
      {nextTier && (
        <div style={{
          padding: '16px',
          background: 'rgba(59, 130, 246, 0.1)',
          borderRadius: '8px',
          border: '2px solid #3b82f6',
          marginBottom: '24px'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
            <div>
              <div style={{ fontSize: '14px', color: '#3b82f6', marginBottom: '4px' }}>
                Prochain tier
              </div>
              <div style={{ fontSize: '20px', fontWeight: 'bold' }}>
                {nextTier.name}
              </div>
              <div style={{ fontSize: '13px', color: 'var(--muted)', marginTop: '4px' }}>
                Requis: {fmt.number(nextTier.requiredStake)} CRO
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '4px' }}>
                CRO manquants
              </div>
              <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#f59e0b' }}>
                {fmt.number(croNeededForNext)}
              </div>
              <div style={{ fontSize: '12px', color: 'var(--muted)' }}>
                ≈ {fmt.currency(usdNeededForNext)}
              </div>
            </div>
          </div>

          {/* Progress Bar */}
          <div style={{ marginTop: '12px' }}>
            <div style={{
              height: '8px',
              background: 'rgba(255,255,255,0.1)',
              borderRadius: '4px',
              overflow: 'hidden',
              position: 'relative'
            }}>
              <div style={{
                height: '100%',
                width: `${progressToNext}%`,
                background: 'linear-gradient(90deg, #3b82f6 0%, #10b981 100%)',
                borderRadius: '4px',
                transition: 'width 0.3s ease'
              }} />
            </div>
            <div style={{
              fontSize: '12px',
              color: 'var(--muted)',
              marginTop: '4px',
              textAlign: 'right'
            }}>
              {progressToNext.toFixed(1)}% vers {nextTier.name}
            </div>
          </div>

          {/* Benefits Unlock Preview */}
          <div style={{
            marginTop: '16px',
            paddingTop: '16px',
            borderTop: '1px solid rgba(255,255,255,0.1)'
          }}>
            <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px', color: '#3b82f6' }}>
              ✨ Avantages débloqués:
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {nextTier.benefits.map((benefit, idx) => (
                <span
                  key={idx}
                  style={{
                    padding: '4px 12px',
                    background: nextTier.color + '30',
                    border: `1px solid ${nextTier.color}`,
                    borderRadius: '12px',
                    fontSize: '12px',
                    color: '#fff'
                  }}
                >
                  {benefit}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Timeline Visualization */}
      <div style={{ position: 'relative', paddingTop: '20px' }}>
        {/* Horizontal Line */}
        <div style={{
          position: 'absolute',
          top: '50px',
          left: '0',
          right: '0',
          height: '4px',
          background: 'linear-gradient(90deg, ' + TIERS.map((t, i) => {
            if (i <= currentTierIndex) return t.color;
            return 'rgba(255,255,255,0.2)';
          }).join(', ') + ')',
          borderRadius: '2px'
        }} />

        {/* Tier Nodes */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${TIERS.length}, 1fr)`,
          gap: '0px',
          position: 'relative'
        }}>
          {TIERS.map((tier, index) => {
            const isCompleted = index <= currentTierIndex;
            const isCurrent = index === currentTierIndex;
            const isNext = index === currentTierIndex + 1;

            return (
              <div
                key={tier.tier}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  position: 'relative',
                  zIndex: isNext ? 10 : isCurrent ? 20 : 1
                }}
              >
                {/* Node Circle */}
                <div
                  style={{
                    width: isCurrent ? '48px' : isNext ? '40px' : '32px',
                    height: isCurrent ? '48px' : isNext ? '40px' : '32px',
                    borderRadius: '50%',
                    background: isCompleted ? tier.color : 'rgba(255,255,255,0.1)',
                    border: isCurrent ? `4px solid ${tier.color}` : isNext ? `3px solid #3b82f6` : `2px solid ${isCompleted ? tier.color : 'rgba(255,255,255,0.2)'}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '12px',
                    transition: 'all 0.3s ease',
                    boxShadow: isCurrent ? `0 0 20px ${tier.color}` : isNext ? '0 0 15px #3b82f6' : 'none'
                  }}
                >
                  {isCompleted ? (
                    <Icons.CheckCircle size={isCurrent ? 24 : 16} style={{ color: '#fff' }} />
                  ) : isNext ? (
                    <Icons.ArrowRight size={20} style={{ color: '#3b82f6' }} />
                  ) : (
                    <Icons.Lock size={16} style={{ color: 'rgba(255,255,255,0.3)' }} />
                  )}
                </div>

                {/* Tier Info */}
                <div style={{
                  textAlign: 'center',
                  fontSize: isCurrent ? '14px' : '12px',
                  fontWeight: isCurrent ? 600 : 400
                }}>
                  <div style={{
                    color: isCompleted ? tier.color : isNext ? '#3b82f6' : 'var(--muted)',
                    marginBottom: '4px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: '100px'
                  }}>
                    {tier.name}
                  </div>
                  <div style={{
                    fontSize: '11px',
                    color: 'var(--muted)'
                  }}>
                    {tier.requiredStake === 0 ? 'Free' : `${fmt.number(tier.requiredStake)} CRO`}
                  </div>
                  <div style={{
                    fontSize: '11px',
                    fontWeight: 600,
                    color: isCompleted ? '#10b981' : 'var(--muted)',
                    marginTop: '4px'
                  }}>
                    {tier.cashbackRate}%
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Summary Table */}
      <div style={{
        marginTop: '32px',
        overflowX: 'auto'
      }}>
        <table style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: '13px'
        }}>
          <thead>
            <tr style={{ borderBottom: '2px solid var(--border)' }}>
              <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Tier</th>
              <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600 }}>Stake Required</th>
              <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600 }}>Cashback</th>
              <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600 }}>Staking APR</th>
              <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Benefits</th>
            </tr>
          </thead>
          <tbody>
            {TIERS.map((tier, index) => {
              const isCompleted = index <= currentTierIndex;
              const isCurrent = index === currentTierIndex;
              const croNeeded = Math.max(0, tier.requiredStake - currentStake);

              return (
                <tr
                  key={tier.tier}
                  style={{
                    borderBottom: '1px solid var(--border)',
                    background: isCurrent ? `${tier.color}10` : 'transparent',
                    opacity: isCompleted ? 1 : 0.6
                  }}
                >
                  <td style={{ padding: '12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div
                        style={{
                          width: '12px',
                          height: '12px',
                          borderRadius: '50%',
                          background: tier.color
                        }}
                      />
                      <span style={{ fontWeight: isCurrent ? 600 : 400 }}>
                        {tier.name}
                      </span>
                      {isCurrent && (
                        <span style={{
                          padding: '2px 6px',
                          background: tier.color,
                          color: tier.tier === 'ICY_WHITE' ? '#000' : '#fff',
                          borderRadius: '4px',
                          fontSize: '10px',
                          fontWeight: 600
                        }}>
                          ACTUEL
                        </span>
                      )}
                    </div>
                  </td>
                  <td style={{ padding: '12px', textAlign: 'right' }}>
                    <div style={{ color: 'var(--muted)' }}>
                      {tier.requiredStake === 0 ? 'Free' : fmt.number(tier.requiredStake)}
                    </div>
                    {!isCompleted && croNeeded > 0 && (
                      <div style={{ fontSize: '11px', color: '#f59e0b', marginTop: '2px' }}>
                        +{fmt.number(croNeeded)} needed
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'right', fontWeight: 600 }}>
                    {tier.cashbackRate}%
                  </td>
                  <td style={{ padding: '12px', textAlign: 'right', color: '#10b981' }}>
                    {tier.stakingAPR}%
                  </td>
                  <td style={{ padding: '12px' }}>
                    <div style={{ fontSize: '11px', color: 'var(--muted)' }}>
                      {tier.benefits.slice(0, 2).join(', ')}
                      {tier.benefits.length > 2 && '...'}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Info */}
      {nextTier && (
        <div style={{
          marginTop: '16px',
          padding: '12px',
          background: 'rgba(245, 158, 11, 0.1)',
          borderRadius: '6px',
          borderLeft: '3px solid #f59e0b',
          fontSize: '12px',
          color: 'var(--muted)',
          lineHeight: '1.6'
        }}>
          <strong>💡 Conseil:</strong> Pour atteindre {nextTier.name}, stakez {fmt.number(croNeededForNext)} CRO supplémentaires
          (≈ {fmt.currency(usdNeededForNext)} au prix actuel). Vous débloquerez {nextTier.cashbackRate}% de cashback
          et {nextTier.benefits.length} avantages premium.
        </div>
      )}

      {currentTierIndex === TIERS.length - 1 && (
        <div style={{
          marginTop: '16px',
          padding: '16px',
          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
          borderRadius: '8px',
          textAlign: 'center',
          color: '#fff'
        }}>
          <div style={{ fontSize: '24px', marginBottom: '8px' }}>🎉</div>
          <div style={{ fontSize: '16px', fontWeight: 600, marginBottom: '4px' }}>
            Félicitations!
          </div>
          <div style={{ fontSize: '13px', opacity: 0.9 }}>
            Vous avez atteint le tier maximum Obsidian avec tous les avantages premium!
          </div>
        </div>
      )}
    </div>
  );
}
